import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../firebase';
import './SignPage.css';

export default function SignInPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleEmailPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetSent(false);
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/rrm');
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err?.message || 'Failed to sign in');
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setResetSent(false);
    setBusy(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      navigate('/rrm');
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError(err?.message || 'Failed to sign in with Google');
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setResetSent(false);
    if (!email) {
      setError('Enter your email above, then click “Forgot password?”');
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err?.message || 'Failed to send password reset email');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sign-container">
      <div className="sign-left-panel">
        <div className="sign-background-image" />
        <div className="sign-left-content">
          <div className="sign-left-logo">RRMonitor</div>
          <h1 className="sign-left-title">Sign In</h1>
          <p className="sign-left-subtitle">
            Access the RRM comparison dashboard.
          </p>
        </div>
      </div>

      <div className="sign-right-panel">
        <div className="sign-card">
          <h2 className="sign-header">Welcome</h2>
          {error && <div className="sign-error">{error}</div>}
          {resetSent && (
            <div className="sign-success">
              Password reset email sent. Check your inbox.
            </div>
          )}

          <form className="sign-form" onSubmit={handleEmailPassword}>
            <div className="sign-input-group">
              <div className="sign-label-row">
                <label htmlFor="email" className="sign-label">Email</label>
              </div>
              <input
                id="email"
                type="email"
                className="sign-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="sign-input-group">
              <div className="sign-label-row">
                <label htmlFor="password" className="sign-label">Password</label>
                <button type="button" className="sign-forgot-link" onClick={handleForgotPassword} disabled={busy}>
                  Forgot password?
                </button>
              </div>
              <div className="sign-password-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="sign-input sign-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="sign-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={busy}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="sign-button-group">
              <button type="submit" className="sign-button-login" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign In'}
              </button>
              <button type="button" className="sign-button-help" onClick={handleGoogle} disabled={busy}>
                Continue with Google
              </button>
            </div>
          </form>

          <div className="sign-bottom-link">
            Don&apos;t have an account?{' '}
            <button className="sign-link-button-inline" type="button" onClick={() => navigate('/signup')}>
              Sign up
            </button>
          </div>

          <button
            className="sign-link-button-dev"
            type="button"
            onClick={() => {
              localStorage.setItem('devBypass', 'true');
              navigate('/rrm');
            }}
          >
            🔓 Skip Auth (Dev Mode)
          </button>
        </div>
      </div>
    </div>
  );
}


